import { DateTime } from 'luxon';
import type { JSX } from 'react';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { SingleSelection } from '../../../ui/labeled-elements/SingleSelection.tsx';
import * as TomCollectionsState from '../../state/TomCollectionsState.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import * as Text from '../../../common/i18n/Text.ts';
import { dateFilterTexts } from '../../i18n/CollectionsTexts.ts';
import * as CollectionsActions from '../../actions/CollectionsActions.ts';
import { DatePicker } from '../../../ui/date-picker/DatePicker.tsx';
import {
    HalfSlideIn,
    SecondLevelHeader,
    SlideInBody,
    SlideInHeaderTexts,
} from '../../../ui/slidein/Slidein.tsx';
import { DateHeader } from '../../../clearance-permit-list/components/shared/DateHeader.tsx';

export interface DateFilterTexts {
    filterTitle: Text.Translation;
    from: Text.Translation;
    to: Text.Translation;
    unlimited: Text.Translation;
}

export function DateFilterHalfSlideIn() {
    const { storeState, update } = useStore(store => ({
        filter: TomCollectionsState.Filter.get(store),
    }));
    const { language } = useLanguage();

    function renderValidFrom(): JSX.Element | null {
        const texts = dateFilterTexts[language];
        if (!storeState.filter.validDateSelectionVisible) {
            return null;
        }
        return (
            <div>
                <DateHeader
                    label={texts.from()}
                    date={storeState.filter.validFrom}
                />
                <DatePicker
                    currentDate={DateTime.now()}
                    selectedDate={storeState.filter.validFrom}
                    onSelect={d =>
                        update(store =>
                            CollectionsActions.setValidFrom(store, d),
                        )
                    }
                    language={language}
                />
            </div>
        );
    }

    function renderToDatePicker(): JSX.Element {
        const texts = dateFilterTexts[language];
        return (
            <div style={{ marginTop: 32 }}>
                <DateHeader
                    label={texts.to()}
                    date={storeState.filter.validTo}
                />
                <DatePicker
                    currentDate={DateTime.now()}
                    selectedDate={storeState.filter.validTo}
                    onSelect={d =>
                        update(store => CollectionsActions.setValidTo(store, d))
                    }
                    language={language}
                />
            </div>
        );
    }

    const texts = dateFilterTexts[language];
    return (
        <HalfSlideIn open={storeState.filter.validDateSelectionVisible}>
            <SecondLevelHeader
                onClose={() =>
                    update(store =>
                        TomCollectionsState.Filter.stateWrite(store, {
                            validDateSelectionVisible: false,
                        }),
                    )
                }
            >
                <SlideInHeaderTexts
                    subtitle={''}
                    title={texts.filterTitle()}
                    hasLeftIcon={false}
                />
            </SecondLevelHeader>
            <SlideInBody>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {renderValidFrom()}
                    {renderToDatePicker()}
                </div>
            </SlideInBody>
        </HalfSlideIn>
    );
}

export function DateFilterSelection() {
    const { storeState, update } = useStore(store => ({
        filter: TomCollectionsState.Filter.get(store),
    }));
    const { language } = useLanguage();

    const texts = dateFilterTexts[language];
    const selection = Formatter.openRange(
        storeState.filter.validFrom,
        storeState.filter.validTo,
        Formatter.dayMonthYear,
        texts.unlimited(),
    );
    const write = (s: Partial<TomCollectionsState.Filter.State>) =>
        update(store => TomCollectionsState.Filter.stateWrite(store, s));

    const clear = TomCollectionsState.Filter.dateFilterActive(storeState.filter)
        ? () => update(CollectionsActions.clearValidDate)
        : null;

    return (
        <div>
            <SingleSelection
                label={texts.filterTitle()}
                focused={false}
                selection={selection}
                onClick={() => write({ validDateSelectionVisible: true })}
                onClear={clear}
            />
        </div>
    );
}
